import React, {
  useState,
  useMemo,
  useRef,
  useEffect,
  useCallback,
} from "react";
import { FlightDetails, ConnectionFlight } from "./types";
import SearchResults from "./SearchResults";
import {
  fetchFlights,
  getLastRoute,
  getDepartureCodes,
  getArrivalCodes,
} from "apis/loadsServices";
import { useGlobalLoader } from "contexts/GlobalLoaderContext";
import Select from "react-select";
import { useToastStore } from "store/toastStore";
import DatePicker from "react-datepicker";
import "react-datepicker/dist/react-datepicker.css";
import SwapIcon from "../../assets/images/swap.png";
import HeroBg from "../../assets/images/top-main2.gif";

// Custom styles for DatePicker
const datePickerStyles = `
  .react-datepicker__day--highlighted {
    background-color: #FFD700 !important;
    color: #000 !important;
  }
  .react-datepicker__day--highlighted:hover {
    background-color: #FFC700 !important;
  }
  #datepicker-portal .react-datepicker-popper {
    z-index: 99999 !important;
  }
`;

// Define interfaces for the API responses
interface DepartureCodeResponse {
  departureCode: string;
  departureName: string;
}

interface ArrivalCodeResponse {
  arrivalCode: string;
  arrivalAirportName: string;
}

interface LastRouteResponse {
  lastSearchFlightNumber: string | null;
  lastSearchRoute: {
    source: string;
    destination: string;
    date: string;
  };
}
interface SearchByRouteProps {
  onShowSearchResults?: (show: boolean) => void;
  onPostRequestSuccess?: () => void;
  tabButtons?: React.ReactNode;
  onBack?: () => void;
  readOnly?: boolean;
  breadcrumbLabel?: string;
}

const formatDateLocal = (date: Date) => {
  const year = date.getFullYear();
  const month = `${date.getMonth() + 1}`.padStart(2, "0");
  const day = `${date.getDate()}`.padStart(2, "0");
  return `${year}-${month}-${day}`;
};

// localStorage utility functions for last search
const LAST_SEARCH_STORAGE_KEY = "staffTraveler_lastSearch";

interface LastSearchData {
  source: string;
  destination: string;
  dates: string[]; // Array of "YYYY-MM-DD" formatted dates
  savedAt: number; // Timestamp for potential cleanup
}

const saveLastSearchToStorage = (
  source: string,
  destination: string,
  dates: string[],
): void => {
  try {
    const data: LastSearchData = {
      source,
      destination,
      dates,
      savedAt: Date.now(),
    };
    localStorage.setItem(LAST_SEARCH_STORAGE_KEY, JSON.stringify(data));
  } catch (error) {
    console.error("Error saving last search to localStorage:", error);
  }
};

const loadLastSearchFromStorage = (): LastSearchData | null => {
  try {
    const stored = localStorage.getItem(LAST_SEARCH_STORAGE_KEY);
    if (!stored) return null;

    const data: LastSearchData = JSON.parse(stored);
    // Validate data structure
    if (data.source && data.destination && Array.isArray(data.dates)) {
      return data;
    }
    return null;
  } catch (error) {
    console.error("Error loading last search from localStorage:", error);
    return null;
  }
};

// localStorage utility functions for recent locations
const RECENT_FROM_LOCATIONS_KEY = "staffTraveler_recentFromLocations";
const RECENT_TO_LOCATIONS_KEY = "staffTraveler_recentToLocations";

interface RecentLocation {
  code: string;
  name: string;
  city?: string;
  country?: string;
  savedAt: number;
}

const saveRecentLocation = (
  type: "from" | "to",
  code: string,
  name: string,
  city?: string,
  country?: string,
): void => {
  try {
    const key =
      type === "from" ? RECENT_FROM_LOCATIONS_KEY : RECENT_TO_LOCATIONS_KEY;
    const stored = localStorage.getItem(key);
    let recentLocations: RecentLocation[] = stored ? JSON.parse(stored) : [];

    // Remove if already exists (avoid duplicates)
    recentLocations = recentLocations.filter((loc) => loc.code !== code);

    // Add new location to beginning
    recentLocations.unshift({
      code,
      name,
      city,
      country,
      savedAt: Date.now(),
    });

    // Keep only last 5 items
    recentLocations = recentLocations.slice(0, 5);

    localStorage.setItem(key, JSON.stringify(recentLocations));
  } catch (error) {
    console.error(
      `Error saving recent ${type} location to localStorage:`,
      error,
    );
  }
};

const loadRecentLocations = (type: "from" | "to"): RecentLocation[] => {
  try {
    const key =
      type === "from" ? RECENT_FROM_LOCATIONS_KEY : RECENT_TO_LOCATIONS_KEY;
    const stored = localStorage.getItem(key);
    if (!stored) return [];

    const data: RecentLocation[] = JSON.parse(stored);
    // Sort by most recent first (already sorted by unshift, but ensure it)
    return data.sort((a, b) => b.savedAt - a.savedAt).slice(0, 5);
  } catch (error) {
    console.error(
      `Error loading recent ${type} locations from localStorage:`,
      error,
    );
    return [];
  }
};

const SearchByRoute: React.FC<SearchByRouteProps> = ({
  onShowSearchResults,
  onPostRequestSuccess,
  tabButtons,
  onBack,
  readOnly = false,
  breadcrumbLabel = "Loads",
}) => {
  const [selectedDeparture, setSelectedDeparture] = useState<"yes" | "no">(
    "no",
  );
  const [selectedArrival, setSelectedArrival] = useState<"yes" | "no">("no");
  // const [stopsFilter, setStopsFilter] = useState<"none" | "one-or-fewer" | null>("none");
  const [stopsFilter, setStopsFilter] = useState<
    "none" | "one-or-fewer" | "one" | "two" | "three" | null
  >("none");
  const [fromLocation, setFromLocation] = useState<string>("");
  const [toLocation, setToLocation] = useState<string>("");
  const [date, setDate] = useState<string>(
    formatDateLocal(new Date(Date.now() + 86400000)),
  ); // Default to Tomorrow's date
  const [showResults, setShowResults] = useState(false);
  const [errors, setErrors] = useState<{
    from?: string;
    to?: string;
    date?: string;
  }>({});
  const [flights, setFlights] = useState<FlightDetails[]>([]);
  const [connectionFlights, setConnectionFlights] = useState<
    ConnectionFlight[]
  >([]);
  const [isLoadingFlights, setIsLoadingFlights] = useState(false);
  const [departureCodes, setDepartureCodes] = useState<DepartureCodeResponse[]>(
    [],
  );
  const [arrivalCodes, setArrivalCodes] = useState<ArrivalCodeResponse[]>([]);
  const [isLoadingDepartures, setIsLoadingDepartures] = useState(false);
  const [isLoadingArrivals, setIsLoadingArrivals] = useState(false);
  const [isLoadingLastSearch, setIsLoadingLastSearch] = useState(false);
  const { showLoader, hideLoader } = useGlobalLoader();
  const dateInputRef = useRef<HTMLInputElement | null>(null);
  const [hasSearched, setHasSearched] = useState(false);
  const [PageNumber, setPageNumber] = useState(1);
  const [totalFlights, setTotalFlights] = useState(0);
  const [selectedDates, setSelectedDates] = useState<Date[]>([]);
  const [datesInRange, setDatesInRange] = useState<string[]>([]);
  const [isCalendarOpen, setIsCalendarOpen] = useState(false);
  const [showMobileFilters, setShowMobileFilters] = useState(false);

  // Recent locations state
  const [recentFromLocations, setRecentFromLocations] = useState<
    RecentLocation[]
  >([]);
  const [recentToLocations, setRecentToLocations] = useState<RecentLocation[]>(
    [],
  );
  const [isFromCardOpen, setIsFromCardOpen] = useState<boolean>(false);
  const [isToCardOpen, setIsToCardOpen] = useState<boolean>(false);
  const [fromSearchInput, setFromSearchInput] = useState<string>("");
  const [toSearchInput, setToSearchInput] = useState<string>("");

  const flightsPerPage = 9;
  const totalPages = Math.ceil(totalFlights / flightsPerPage);

  // Create options for react-select with combined code and name - memoized to prevent re-renders
  const departureOptions = useMemo(() => {
    return departureCodes.map((code) => ({
      value: code.departureCode,
      label: `${code.departureCode} - ${code.departureName}`,
      isDisabled: code.departureCode === toLocation,
    }));
  }, [departureCodes, toLocation]);

  const arrivalOptions = useMemo(() => {
    return arrivalCodes.map((code) => ({
      value: code.arrivalCode,
      label: `${code.arrivalCode} - ${code.arrivalAirportName}`,
      isDisabled: code.arrivalCode === fromLocation,
    }));
  }, [arrivalCodes, fromLocation]);

  // Load recent locations from localStorage on component mount
  useEffect(() => {
    const fromLocs = loadRecentLocations("from");
    const toLocs = loadRecentLocations("to");
    setRecentFromLocations(fromLocs);
    setRecentToLocations(toLocs);
  }, []);

  // Refs to track last searched values to prevent duplicate API calls
  const lastFromSearchRef = useRef<string>("");
  const lastToSearchRef = useRef<string>("");

  // Fetch departure codes - memoized with useCallback to prevent infinite loops
  const loadDepartureCodes = useCallback(async (inputValue: string) => {
    const trimmedValue = inputValue.trim();

    if (!trimmedValue) {
      return;
    }

    // Check if we're already loading or if this is a duplicate request
    if (lastFromSearchRef.current === trimmedValue) {
      return;
    }

    // Mark as currently searching before making the call
    const currentSearch = trimmedValue;
    lastFromSearchRef.current = currentSearch;
    // setIsLoadingDepartures(true); // <-- moved here, after all early returns

    try {
      const response = await getDepartureCodes(trimmedValue);
      const newData =
        response?.data || (Array.isArray(response) ? response : []);

      // If no results, set empty array
      if (!newData || (Array.isArray(newData) && newData.length === 0)) {
        setDepartureCodes([]);
      } else if (lastFromSearchRef.current === currentSearch) {
        setDepartureCodes((prevCodes) => {
          // Compare to avoid unnecessary updates
          const prevDataStr = JSON.stringify(prevCodes);
          const newDataStr = JSON.stringify(newData);
          if (prevDataStr !== newDataStr) {
            return newData;
          }
          return prevCodes;
        });
      }
    } catch (error) {
      console.error("Error fetching departure codes:", error);
      // Only reset ref if this was the current search
      if (lastFromSearchRef.current === currentSearch) {
        lastFromSearchRef.current = "";
      }
      setDepartureCodes([]); // Show no results on error
    } finally {
      // Only clear loading if this is still the current search
      if (
        lastFromSearchRef.current === currentSearch ||
        lastFromSearchRef.current === ""
      ) {
        setIsLoadingDepartures(false);
      }
    }
  }, []);

  // Fetch arrival codes - memoized with useCallback to prevent infinite loops
  const loadArrivalCodes = useCallback(async (inputValue: string) => {
    const trimmedValue = inputValue.trim();

    if (!trimmedValue) {
      return;
    }

    // Check if we're already loading or if this is a duplicate request
    if (lastToSearchRef.current === trimmedValue) {
      return;
    }

    // Mark as currently searching before making the call
    const currentSearch = trimmedValue;
    lastToSearchRef.current = currentSearch;
    // setIsLoadingArrivals(true); // <-- moved here, after all early returns

    try {
      const response = await getArrivalCodes(trimmedValue);
      const newData =
        response?.data || (Array.isArray(response) ? response : []);

      // If no results, set empty array
      if (!newData || (Array.isArray(newData) && newData.length === 0)) {
        setArrivalCodes([]);
      } else if (lastToSearchRef.current === currentSearch) {
        setArrivalCodes((prevCodes) => {
          // Compare to avoid unnecessary updates
          const prevDataStr = JSON.stringify(prevCodes);
          const newDataStr = JSON.stringify(newData);
          if (prevDataStr !== newDataStr) {
            return newData;
          }
          return prevCodes;
        });
      }
    } catch (error) {
      console.error("Error fetching arrival codes:", error);
      // Only reset ref if this was the current search
      if (lastToSearchRef.current === currentSearch) {
        lastToSearchRef.current = "";
      }
      setArrivalCodes([]); // Show no results on error
    } finally {
      // Only clear loading if this is still the current search
      if (
        lastToSearchRef.current === currentSearch ||
        lastToSearchRef.current === ""
      ) {
        setIsLoadingArrivals(false);
      }
    }
  }, []);

  // Clear "To" field if same value is selected in "From"
  useEffect(() => {
    if (fromLocation && fromLocation === toLocation) {
      setToLocation("");
      setErrors((prev) => ({
        ...prev,
        to: "Departure and arrival locations cannot be the same",
      }));
    }
  }, [fromLocation]);

  // Clear "From" field if same value is selected in "To"
  useEffect(() => {
    if (toLocation && toLocation === fromLocation) {
      setFromLocation("");
      setErrors((prev) => ({
        ...prev,
        from: "Departure and arrival locations cannot be the same",
      }));
    }
  }, [toLocation]);

  // Track previous filter value to detect changes
  // const prevStopsFilter = useRef<"none" | "one-or-fewer" | null>("none");
  const prevStopsFilter = useRef<
    "none" | "one-or-fewer" | "one" | "two" | "three" | null
  >("none");
  // Track previous page number to detect page changes
  const prevPageNumber = useRef<number>(1);

  // Handler for stops filter change - sets loading state immediately
  // const handleStopsFilterChange = (filter: "none" | "one-or-fewer" | null) => {
  const handleStopsFilterChange = (
    filter: "none" | "one-or-fewer" | "one" | "two" | "three" | null,
  ) => {
    // Always set the filter
    const filterChanged = hasSearched && prevStopsFilter.current !== filter;

    // If filter changed and user has searched, set loading state immediately (synchronously)
    if (filterChanged) {
      setIsLoadingFlights(true);
    }

    // Update the filter which will trigger the useEffect
    setStopsFilter(filter);
  };

  // Fetch flights when filter changes (reset to page 1 first)
  useEffect(() => {
    // Only fetch if filter actually changed and user has searched before
    if (hasSearched && prevStopsFilter.current !== stopsFilter) {
      // Reset to page 1 if needed - update prevPageNumber to prevent page change effect
      const needsPageReset = PageNumber !== 1;
      if (needsPageReset) {
        prevPageNumber.current = PageNumber; // Mark that we're resetting, not changing
        setPageNumber(1);
      }

      // Use requestAnimationFrame to ensure React has rendered the skeleton before starting API call
      const fetchWithFilter = async () => {
        try {
          // Wait for React to render the skeleton
          await new Promise((resolve) => {
            requestAnimationFrame(() => {
              requestAnimationFrame(() => {
                resolve(undefined);
              });
            });
          });

          // Additional small delay to ensure skeleton is visible
          await new Promise((resolve) => setTimeout(resolve, 100));

          await getFlights();

          // Scroll to search results
          setTimeout(() => {
            const searchResultsElement =
              document.getElementById("search-results");
            if (searchResultsElement) {
              searchResultsElement.scrollIntoView({
                behavior: "smooth",
                block: "start",
              });
            }
          }, 100);
        } catch (error) {
          console.error("Error fetching flights for filter:", error);
        }
      };

      fetchWithFilter();
    }
    // Update previous filter value after checking
    if (hasSearched) {
      prevStopsFilter.current = stopsFilter;
    }
  }, [stopsFilter, hasSearched, PageNumber]);

  // Handler for page number change - sets loading state immediately
  const handlePageChange = (page: number) => {
    if (hasSearched && PageNumber !== page) {
      // Set loading state immediately - this happens synchronously
      setIsLoadingFlights(true);
      // Then update the page number which will trigger the useEffect
      setPageNumber(page);
    }
  };

  // Fetch flights when page number changes (but not on initial load or filter change)
  useEffect(() => {
    // Only fetch if page actually changed (not initial load or filter reset)
    if (
      hasSearched &&
      prevPageNumber.current !== PageNumber &&
      prevPageNumber.current !== null
    ) {
      const fetchFlightsForPage = async () => {
        try {
          // Wait for React to render the skeleton
          await new Promise((resolve) => {
            requestAnimationFrame(() => {
              requestAnimationFrame(() => {
                resolve(undefined);
              });
            });
          });

          // Additional small delay to ensure skeleton is visible
          await new Promise((resolve) => setTimeout(resolve, 100));

          await getFlights();

          // Scroll to search results after page change
          setTimeout(() => {
            const searchResultsElement =
              document.getElementById("search-results");
            if (searchResultsElement) {
              searchResultsElement.scrollIntoView({
                behavior: "smooth",
                block: "start",
              });
            }
          }, 100);
        } catch (error) {
          console.error("Error fetching flights for page:", error);
        }
      };
      fetchFlightsForPage();
    }
    // Update previous page number after checking
    if (hasSearched) {
      prevPageNumber.current = PageNumber;
    }
  }, [PageNumber, hasSearched]);

  // No auto-reset of filter — if no results found, show empty state instead of silently switching.

  // Clear search function - resets all fields and search state
  const handleClearSearch = () => {
    // Clear form fields
    setFromLocation("");
    setToLocation("");
    setSelectedDates([]);
    setDatesInRange([]);
    setDate(formatDateLocal(new Date(Date.now() + 86400000))); // Reset to tomorrow

    // Close cards and clear search inputs
    setIsFromCardOpen(false);
    setIsToCardOpen(false);
    setFromSearchInput("");
    setToSearchInput("");

    // Clear search results
    setFlights([]);
    setConnectionFlights([]);
    setTotalFlights(0);
    setShowResults(false);

    // Reset search state
    setHasSearched(false);
    setPageNumber(1);
    prevPageNumber.current = 1;
    prevStopsFilter.current = "none";

    // Reset filters
    setStopsFilter("none");
    setSelectedDeparture("no");
    setSelectedArrival("no");

    // Clear errors
    setErrors({});

    // Hide results
    onShowSearchResults?.(false);
  };

  // Optimized last search function - loads from localStorage first, falls back to API
  const handleLastSearchClick = async () => {
    setIsLoadingLastSearch(true);
    setIsLoadingFlights(true); // Show skeleton for search

    try {
      // First check localStorage
      let lastSearchData: LastSearchData | null = loadLastSearchFromStorage();

      // If not found in localStorage, fetch from API as fallback
      if (!lastSearchData) {
        showLoader(); // Show global loader for API call
        try {
          const response = await getLastRoute();
          const lastRouteData: LastRouteResponse = response.data || response;

          if (lastRouteData && lastRouteData.lastSearchRoute) {
            const {
              source,
              destination,
              date: lastDate,
            } = lastRouteData.lastSearchRoute;

            // Convert single date to array format for storage
            lastSearchData = {
              source,
              destination,
              dates: [lastDate],
              savedAt: Date.now(),
            };

            // Save to localStorage for future use
            saveLastSearchToStorage(source, destination, [lastDate]);
          } else {
            console.log("No last search data available");
            hideLoader();
            setIsLoadingLastSearch(false);
            setIsLoadingFlights(false);
            return;
          }
        } catch (error) {
          console.error("Error fetching last search from API:", error);
          hideLoader();
          setIsLoadingLastSearch(false);
          setIsLoadingFlights(false);
          return;
        } finally {
          hideLoader();
        }
      }

      // Populate fields with stored data
      if (lastSearchData) {
        const { source, destination, dates } = lastSearchData;

        // Set locations
        setFromLocation(source);
        setToLocation(destination);

        // Convert date strings to Date objects for calendar
        const dateObjects = dates
          .map((dateStr) => {
            const date = new Date(dateStr);
            // Validate date
            if (isNaN(date.getTime())) {
              return null;
            }
            return date;
          })
          .filter((date): date is Date => date !== null);

        // Set dates
        setSelectedDates(dateObjects);
        setDatesInRange(dates);

        // Set date state for backward compatibility (use first date if available)
        if (dates.length > 0) {
          setDate(dates[0]);
        }

        // Clear any errors
        setErrors({});

        // Set search state
        setHasSearched(true);
        onShowSearchResults?.(true);
        prevPageNumber.current = 1;
        setPageNumber(1);

        // Wait for state updates to complete, then auto-execute search with direct values
        await new Promise((resolve) => {
          requestAnimationFrame(() => {
            requestAnimationFrame(() => {
              resolve(undefined);
            });
          });
        });

        // Call getFlights with direct values to ensure they're used
        await getFlights({
          source,
          destination,
          dates,
          pageNumber: 1,
        });

        // Scroll to search results
        setTimeout(() => {
          const searchResultsElement =
            document.getElementById("search-results");
          if (searchResultsElement) {
            searchResultsElement.scrollIntoView({
              behavior: "smooth",
              block: "start",
            });
          }
        }, 100);
      }
    } catch (error) {
      console.error("Error loading last search:", error);
    } finally {
      setIsLoadingLastSearch(false);
      // Note: isLoadingFlights will be set to false by getFlights() finally block
    }
  };

  const getFlights = async (overrideParams?: {
    source?: string;
    destination?: string;
    dates?: string[];
    pageNumber?: number;
  }) => {
    // Set loading state - will be reset in finally block
    setIsLoadingFlights(true);
    try {
      // Map stops filter to API parameters
      let Stops: number | undefined;
      let IncludeDirect: boolean | undefined;
      let IncludeConnections: boolean | undefined;

      if (stopsFilter === "none") {
        IncludeDirect = true;
        IncludeConnections = false;
        Stops = 0;
      } else if (stopsFilter === "one-or-fewer") {
        IncludeDirect = false;
        IncludeConnections = true;
      } else if (stopsFilter === "one") {
        Stops = 1;
      } else if (stopsFilter === "two") {
        Stops = 2;
      } else if (stopsFilter === "three") {
        Stops = 3;
      } else {
        // Default: non-stop only (since default is "none")
        IncludeDirect = true;
        IncludeConnections = false;
      }

      // Use override params if provided, otherwise use state values
      const sourceValue = overrideParams?.source ?? fromLocation;
      const destinationValue = overrideParams?.destination ?? toLocation;
      const datesValue = overrideParams?.dates ?? datesInRange;
      const pageNumberValue = overrideParams?.pageNumber ?? PageNumber;

      // Prepare params as plain object
      const params = {
        Source: sourceValue,
        Destination: destinationValue,
        IsNearbyDeparture: selectedDeparture === "yes",
        IsNearbyArrival: selectedArrival === "yes",
        Dates: datesValue,
        PageNumber: pageNumberValue,
        PageSize: flightsPerPage,
        IncludeDirect,
        IncludeConnections,
        ...(Stops !== undefined ? { Stops } : {}),
      };

      console.log("Sending params:", params);

      // Fetch flights (GET request)
      const response = await fetchFlights(params);
      const rawFlightData = response.data.items || [];
      const connectionsData = (response.data.connections ||
        []) as ConnectionFlight[];

      // If we requested connections but API returned only direct flights,
      // still show results rather than silently reverting the filter.

      const flightData = rawFlightData as FlightDetails[];

      // Generate composite IDs for direct flights
      const flightsWithCompositeIds = flightData.map((flight) => {
        const baseId = flight.id ?? `${Date.now()}-${Math.random()}`;
        const scheduleDate = flight.scheduleDate || flight.date;
        const compositeId = scheduleDate
          ? `${baseId}_${scheduleDate.replace(/[^0-9]/g, "")}`
          : String(baseId);

        return {
          ...flight,
          compositeId,
        };
      });

      // Generate composite IDs for connection flights
      const connectionsWithCompositeIds = connectionsData.map((connection) => {
        // Generate composite IDs for each segment
        const segmentsWithIds = connection.segments.map((segment) => {
          const baseId = segment.id ?? `${Date.now()}-${Math.random()}`;
          const departureTime = segment.departureTime;
          const compositeId = departureTime
            ? `${baseId}_${departureTime.replace(/[^0-9]/g, "")}`
            : String(baseId);

          return {
            ...segment,
            compositeId,
          };
        });

        // Generate composite ID for the connection flight itself
        const firstSegment = segmentsWithIds[0];
        const connectionCompositeId = firstSegment?.compositeId
          ? `connection_${firstSegment.compositeId}_${connection.overallDepartureTime.replace(/[^0-9]/g, "")}`
          : `connection_${Date.now()}_${Math.random()}`;

        return {
          ...connection,
          segments: segmentsWithIds,
          compositeId: connectionCompositeId,
        };
      });

      setFlights(flightsWithCompositeIds);
      setConnectionFlights(connectionsWithCompositeIds);

      // Use data.total for all filters (direct flights and connection flights)
      if (response.data.total !== undefined) {
        setTotalFlights(response.data.total);
      } else {
        // Fallback: estimate based on current page results
        const estimatedTotal =
          flightData.length + connectionsData.length < flightsPerPage
            ? (PageNumber - 1) * flightsPerPage +
              flightData.length +
              connectionsData.length
            : PageNumber * flightsPerPage;
        setTotalFlights(estimatedTotal);
      }

      setShowResults(flightData.length > 0 || connectionsData.length > 0);
      console.log("Flight data:", flightData);
      console.log("Connection data:", connectionsData);
    } catch (err: any) {
      const apiMessage = err.response?.data?.message;
      useToastStore
        .getState()
        .showToast(apiMessage || "Failed to fetch flights.", "error");
      setFlights([]);
      setConnectionFlights([]);
      setTotalFlights(0);
      setShowResults(false);
    } finally {
      setIsLoadingFlights(false);
    }
  };

  const handleSearchClick = async () => {
    setHasSearched(true);
    onShowSearchResults?.(true);
    prevPageNumber.current = 1; // Reset prev page number for new search
    setPageNumber(1); // Reset to first page on new search
    // Simple validation check when the search button is clicked
    const newErrors: typeof errors = {};
    if (!fromLocation) newErrors.from = "*Please select a departure location.";
    if (!toLocation) newErrors.to = "*Please select an arrival location.";
    if (selectedDates.length === 0)
      newErrors.date = "*Please select at least one date.";
    if (fromLocation === toLocation && fromLocation) {
      newErrors.from = "Departure and arrival locations cannot be the same.";
      newErrors.to = "Departure and arrival locations cannot be the same.";
    }
    setErrors(newErrors);

    if (Object.keys(newErrors).length > 0) return;

    try {
      // Set loading state first to show skeleton
      setIsLoadingFlights(true);
      // Small delay to ensure skeleton renders
      await new Promise((resolve) => setTimeout(resolve, 50));
      await getFlights();

      // Save search data to localStorage after successful search
      if (fromLocation && toLocation && datesInRange.length > 0) {
        saveLastSearchToStorage(fromLocation, toLocation, datesInRange);
      }

      // Scroll to search results after a brief delay to ensure content is rendered
      setTimeout(() => {
        const searchResultsElement = document.getElementById("search-results");
        if (searchResultsElement) {
          searchResultsElement.scrollIntoView({
            behavior: "smooth",
            block: "start",
          });
        }
      }, 100);
    } catch (error) {
      console.error("Error fetching flights:", error);
    }
  };

  const getDatesBetween = (start: Date, end: Date) => {
    const dates: string[] = [];
    const current = new Date(start);
    while (current <= end) {
      dates.push(formatDateLocal(current));
      current.setDate(current.getDate() + 1);
    }
    return dates;
  };

  const handleDateChange = (date: Date | null) => {
    if (date) {
      const dateStr = formatDateLocal(date);
      const dateIndex = selectedDates.findIndex(
        (d) => formatDateLocal(d) === dateStr,
      );

      if (dateIndex > -1) {
        // Date already selected, remove it
        const newDates = selectedDates.filter(
          (_, index) => index !== dateIndex,
        );
        setSelectedDates(newDates);
      } else {
        // Add new date
        const newDates = [...selectedDates, date].sort(
          (a, b) => a.getTime() - b.getTime(),
        );
        setSelectedDates(newDates);
      }

      // Clear date error when a date is selected
      if (errors.date) {
        setErrors((prev) => ({ ...prev, date: undefined }));
      }
    }
  };

  const handleSaveDates = async () => {
    const formattedDates = selectedDates.map((d) => formatDateLocal(d));
    setDatesInRange(formattedDates);
    setIsCalendarOpen(false);

    // Auto-trigger search if all fields are filled
    if (fromLocation && toLocation && selectedDates.length > 0) {
      setTimeout(async () => {
        try {
          // Set search state
          setHasSearched(true);
          prevPageNumber.current = 1;
          setPageNumber(1);

          // Clear any previous errors
          setErrors({});

          // Set loading state first to show skeleton
          setIsLoadingFlights(true);
          // Small delay to ensure skeleton renders
          await new Promise((resolve) => setTimeout(resolve, 50));

          // Call getFlights with override params to ensure dates are passed
          await getFlights({
            source: fromLocation,
            destination: toLocation,
            dates: formattedDates,
            pageNumber: 1,
          });

          // Save search data to localStorage after successful search
          saveLastSearchToStorage(fromLocation, toLocation, formattedDates);

          // Scroll to search results after a brief delay to ensure content is rendered
          setTimeout(() => {
            const searchResultsElement =
              document.getElementById("search-results");
            if (searchResultsElement) {
              searchResultsElement.scrollIntoView({
                behavior: "smooth",
                block: "start",
              });
            }
          }, 100);
        } catch (error) {
          console.error("Error fetching flights:", error);
        }
      }, 100);
    }
  };

  const clearAllDates = () => {
    setSelectedDates([]);
    setDatesInRange([]);
  };

  // Location Search Card Component
  interface LocationSearchCardProps {
    type: "from" | "to";
    value: string;
    onChange: (code: string, name: string) => void;
    recentLocations: RecentLocation[];
    isOpen: boolean;
    onOpen: () => void;
    onClose: () => void;
    placeholder: string;
    searchInput: string;
    setSearchInput: (value: string) => void;
    options: Array<{
      value: string;
      label: string;
      code?: string;
      name?: string;
      isDisabled?: boolean;
    }>;
    isLoading: boolean;
    label: string;
    onSearch: (inputValue: string) => Promise<void> | void;
  }

  const LocationSearchCard: React.FC<LocationSearchCardProps> = ({
    type,
    value,
    onChange,
    recentLocations,
    isOpen,
    onClose,
    placeholder,
    searchInput,
    setSearchInput,
    options,
    isLoading,
    label,
    onSearch,
  }) => {
    const cardRef = useRef<HTMLDivElement>(null);
    const searchInputRef = useRef<HTMLInputElement>(null);
    // Track last searched value to prevent duplicate calls
    const lastSearchedRef = useRef<string>("");
    // Track if a search is currently in progress to prevent overlapping calls
    const isSearchingRef = useRef<boolean>(false);
    // Ref to hold debounce timer id so we can clear it reliably
    const debounceTimerRef = useRef<number | null>(null);

    // Close on outside click
    useEffect(() => {
      if (!isOpen) return;

      const handleClickOutside = (event: MouseEvent) => {
        if (
          cardRef.current &&
          !cardRef.current.contains(event.target as Node)
        ) {
          onClose();
        }
      };

      document.addEventListener("mousedown", handleClickOutside);
      return () => {
        document.removeEventListener("mousedown", handleClickOutside);
      };
    }, [isOpen, onClose]);

    // Focus search input when card opens and reset last searched value
    useEffect(() => {
      if (isOpen && searchInputRef.current) {
        // Reset when card opens so user can search again
        lastSearchedRef.current = "";
        // clear any pending timer
        if (debounceTimerRef.current) {
          clearTimeout(debounceTimerRef.current);
          debounceTimerRef.current = null;
        }
        setTimeout(() => {
          searchInputRef.current?.focus();
        }, 100);
      } else if (!isOpen) {
        // Reset when card closes
        lastSearchedRef.current = "";
        isSearchingRef.current = false;
        if (debounceTimerRef.current) {
          clearTimeout(debounceTimerRef.current);
          debounceTimerRef.current = null;
        }
      }
    }, [isOpen]);

    const handleSelectLocation = (code: string, name: string) => {
      onChange(code, name);
      setSearchInput("");
      // clear any pending debounce when selecting
      if (debounceTimerRef.current) {
        clearTimeout(debounceTimerRef.current);
        debounceTimerRef.current = null;
      }
      onClose();
    };

    const handleSearchChange = (e: React.ChangeEvent<HTMLInputElement>) => {
      const inputValue = e.target.value;
      // Update input immediately for responsive feel
      setSearchInput(inputValue);
      // Reset last searched when input is cleared
      if (inputValue.trim().length === 0) {
        lastSearchedRef.current = "";
        if (debounceTimerRef.current) {
          clearTimeout(debounceTimerRef.current);
          debounceTimerRef.current = null;
        }
      }
    };

    // Debounced search effect (robust, clears/awaits properly)
    useEffect(() => {
      // If card is closed, ensure we clean up state and timers
      if (!isOpen) {
        lastSearchedRef.current = "";
        isSearchingRef.current = false;
        if (debounceTimerRef.current) {
          clearTimeout(debounceTimerRef.current);
          debounceTimerRef.current = null;
        }
        return;
      }

      const trimmedInput = searchInput.trim();

      // Don't search if input is empty
      if (trimmedInput.length === 0) {
        lastSearchedRef.current = "";
        isSearchingRef.current = false;
        if (debounceTimerRef.current) {
          clearTimeout(debounceTimerRef.current);
          debounceTimerRef.current = null;
        }
        return;
      }

      // Don't search if we already searched for this exact value
      if (lastSearchedRef.current === trimmedInput) {
        return;
      }

      // Clear any existing timer and set a fresh one
      if (debounceTimerRef.current) {
        clearTimeout(debounceTimerRef.current);
        debounceTimerRef.current = null;
      }

      debounceTimerRef.current = window.setTimeout(async () => {
        // If value changed while waiting, bail out
        if (searchInput.trim() !== trimmedInput) {
          debounceTimerRef.current = null;
          return;
        }

        // If already searching, don't overlap
        if (isSearchingRef.current) {
          debounceTimerRef.current = null;
          return;
        }

        isSearchingRef.current = true;
        lastSearchedRef.current = trimmedInput;

        try {
          const result = onSearch(trimmedInput);
          if (result && typeof (result as Promise<any>).then === "function") {
            await (result as Promise<any>);
          }
        } catch (error) {
          // Reset last searched on error so user can retry
          if (lastSearchedRef.current === trimmedInput) {
            lastSearchedRef.current = "";
          }
          console.error("Error in onSearch:", error);
        } finally {
          isSearchingRef.current = false;
          debounceTimerRef.current = null;
        }
      }, 500);

      return () => {
        if (debounceTimerRef.current) {
          clearTimeout(debounceTimerRef.current);
          debounceTimerRef.current = null;
        }
      };
    }, [searchInput, isOpen, onSearch]);

    if (!isOpen) return null;

    const showRecentLocations =
      searchInput.trim().length === 0 && recentLocations.length > 0;
    const showSearchResults =
      searchInput.trim().length > 0 && options.length > 0;

    return (
      <div
        ref={cardRef}
        className="absolute top-full left-0 right-0 mt-2 bg-white border border-gray-200 rounded-lg shadow-lg z-50 max-h-[600px] w-full overflow-hidden flex flex-col"
      >
        {/* Header with Close Button */}
        <div className="p-4 border-b border-gray-100 flex items-center justify-between">
          <p className="text-sm font-semibold text-gray-700">
            {type === "from"
              ? "Select Departure Airport"
              : "Select Arrival Airport"}
          </p>
          <button
            type="button"
            onClick={onClose}
            className="text-gray-400 hover:text-gray-600 transition-colors"
          >
            <i className="st-close text-lg"></i>
          </button>
        </div>

        {/* Search Input Section - At Top */}
        <div className="p-4 border-b border-gray-100">
          <div className="relative">
            <input
              ref={searchInputRef}
              type="text"
              value={searchInput}
              onChange={handleSearchChange}
              placeholder={placeholder}
              className="w-full p-3 pr-10 border border-gray-200 rounded-lg bg-lightblue-100 text-sm text-grease-100 focus:outline-none focus:ring-2 focus:ring-royalblue-100 transition-all duration-150"
              autoComplete="off"
            />
            {isLoading && (
              <div className="absolute right-3 top-1/2 -translate-y-1/2">
                <div className="w-5 h-5 border-2 border-royalblue-100 border-t-transparent rounded-full animate-spin"></div>
              </div>
            )}
          </div>
        </div>

        {/* Content Section - Recent Locations or Search Results */}
        <div className="overflow-y-auto flex-1 max-h-[450px]">
          {/* Recent Locations Section - Show when input is empty */}
          {showRecentLocations && (
            <div>
              <div className="px-4 py-2 bg-gray-50 border-b border-gray-100">
                <p className="text-xs font-semibold text-gray-600 uppercase">
                  {type === "from" ? "Recent Airports" : "Recent Destinations"}
                </p>
              </div>
              <div className="divide-y divide-gray-100">
                {recentLocations.map((location) => (
                  <button
                    key={location.code}
                    type="button"
                    onClick={() =>
                      handleSelectLocation(location.code, location.name)
                    }
                    className="w-full px-4 py-3 text-left hover:bg-gray-50 transition-colors"
                  >
                    <div className="flex-1">
                      <div className="flex items-center gap-2">
                        <span className="font-bold text-gray-900">
                          {location.code}
                        </span>
                        <span className="text-sm text-gray-700">
                          {location.name}
                        </span>
                      </div>
                      {(location.city || location.country) && (
                        <p className="text-xs text-gray-500 mt-1">
                          {[location.city, location.country]
                            .filter(Boolean)
                            .join(", ")}
                        </p>
                      )}
                    </div>
                  </button>
                ))}
              </div>
            </div>
          )}

          {/* Search Results - Show when typing */}
          {showSearchResults && (
            <div>
              <div className="px-4 py-2 bg-gray-50 border-b border-gray-100">
                <p className="text-xs font-semibold text-gray-600 uppercase">
                  Search Results
                </p>
              </div>
              <div className="divide-y divide-gray-100">
                {options
                  .filter((option) => !option.isDisabled)
                  .map((option) => (
                    <button
                      key={option.value}
                      type="button"
                      onClick={() => {
                        const name = option.label.includes(" - ")
                          ? option.label.split(" - ")[1]
                          : option.label;
                        handleSelectLocation(option.value, name);
                      }}
                      className="w-full px-4 py-3 text-left hover:bg-gray-50 transition-colors"
                    >
                      <div className="flex items-center gap-2">
                        <span className="font-bold text-gray-900">
                          {option.value}
                        </span>
                        <span className="text-sm text-gray-700">
                          {option.label.includes(" - ")
                            ? option.label.split(" - ")[1]
                            : option.label}
                        </span>
                      </div>
                    </button>
                  ))}
              </div>
            </div>
          )}

          {/* No Results Message */}
          {searchInput.trim().length > 0 &&
            !isLoading &&
            options.length === 0 && (
              <div className="text-center py-8 text-gray-500 text-sm">
                No airports found
              </div>
            )}
        </div>
      </div>
    );
  };

  const searchResults = useMemo(() => {
    if (!hasSearched) return null;
    onShowSearchResults?.(true);
    return (
      <div id="search-results">
        {/* Mobile: back + filter bar */}
        <div className="flex items-center justify-between md:hidden mt-4 mb-2 px-1">
          <div className="flex items-center gap-2">
            <button
              type="button"
              className="flex items-center justify-center w-8 h-8 rounded-lg bg-white shadow-sm hover:bg-[#F3F4F6] transition-colors"
              onClick={() => {
                handleClearSearch();
                onBack?.();
              }}
            >
              <svg className="w-4 h-4 text-[#1F2937]" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M10 19l-7-7m0 0l7-7m-7 7h18" />
              </svg>
            </button>
            <span className="text-[#1F2937] text-sm font-bold">{breadcrumbLabel}</span>
            <span className="text-gray-400 text-xs">&gt;</span>
            <span className="text-[#1F2937] text-sm font-medium">Route</span>
          </div>
          <button
            type="button"
            onClick={() => setShowMobileFilters(true)}
            className="flex items-center gap-1.5 px-3 py-1.5 border border-gray-200 rounded-lg text-xs text-gray-600 hover:bg-gray-50 transition-colors"
          >
            <svg className="w-3.5 h-3.5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M3 4a1 1 0 011-1h16a1 1 0 011 1v2.586a1 1 0 01-.293.707l-6.414 6.414a1 1 0 00-.293.707V17l-4 4v-6.586a1 1 0 00-.293-.707L3.293 7.293A1 1 0 013 6.586V4z" />
            </svg>
            Filters
          </button>
        </div>

        {/* Mobile Filter Drawer */}
        {showMobileFilters && (
          <div className="fixed inset-0 z-50 md:hidden">
            <div className="absolute inset-0 bg-black/40" onClick={() => setShowMobileFilters(false)} />
            <div className="absolute bottom-0 left-0 right-0 bg-white rounded-t-2xl p-5 max-h-[70vh] overflow-y-auto">
              <div className="flex items-center justify-between mb-4">
                <p className="text-[#1F2937] text-base font-bold">Filters</p>
                <button type="button" onClick={() => setShowMobileFilters(false)} className="text-gray-400 hover:text-gray-600 p-1">
                  <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                  </svg>
                </button>
              </div>
              <p className="text-[#9CA3AF] text-xs mb-4">Showing {totalFlights || 0} results</p>
              <p className="text-[#1F2937] text-sm font-semibold mb-3">Stops</p>
              <div className="flex flex-col gap-3">
                {((readOnly
                  ? (["none", "one", "two", "three"] as const)
                  : (["none", "one", "two", "three", "one-or-fewer"] as const))
                ).map((val) => {
                  const labels: Record<string, string> = { none: "Non-stop", one: "1 Stop", two: "2 Stops", three: "3 Stops", "one-or-fewer": "Connected" };
                  return (
                    <label key={val} className="flex items-center cursor-pointer group">
                      <div className="flex items-center gap-2">
                        <input
                          type="radio"
                          name="stops-mobile-route"
                          checked={stopsFilter === val}
                          onChange={() => { handleStopsFilterChange(val); setShowMobileFilters(false); }}
                          className="w-4 h-4 text-[#FBBF24] border-gray-300 focus:ring-[#FBBF24] accent-[#1F2937]"
                        />
                        <span className="text-sm text-[#4B5563] group-hover:text-[#1F2937]">{labels[val]}</span>
                      </div>
                    </label>
                  );
                })}
              </div>
              <button
                type="button"
                onClick={() => { handleStopsFilterChange("none"); setShowMobileFilters(false); }}
                className="w-full mt-5 py-2.5 text-sm font-medium text-gray-600 border border-gray-200 rounded-xl hover:bg-gray-50 transition-colors"
              >
                Reset Filters
              </button>
            </div>
          </div>
        )}

        <SearchResults
          flights={flights}
          connectionFlights={connectionFlights}
          stopsFilter={stopsFilter}
          onStopsFilterChange={handleStopsFilterChange}
          isLoadingFlights={isLoadingFlights}
          show={true}
          searchDate={date}
          selectedDates={datesInRange}
          onPostRequestSuccess={onPostRequestSuccess}
          sourceLabel={fromLocation}
          destinationLabel={toLocation}
          totalResults={totalFlights}
          hideInternalFilters={true}
          hideConnectedFilter={readOnly}
          readOnly={readOnly}
        />
        {totalPages > 1 && (
          <div className="flex justify-center mt-6">
            <div className="inline-flex items-center bg-[#1F2937] rounded-full px-1 py-1 gap-1">
              <button
                onClick={() => handlePageChange(Math.max(1, PageNumber - 1))}
                disabled={PageNumber === 1}
                className="w-8 h-8 flex items-center justify-center rounded-full text-white hover:bg-white/10 transition-colors disabled:opacity-30 disabled:cursor-not-allowed"
              >
                <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 19l-7-7 7-7" />
                </svg>
              </button>
              <span className="text-white text-sm font-medium px-3">
                {PageNumber} / {totalPages}
              </span>
              <button
                onClick={() => handlePageChange(Math.min(totalPages, PageNumber + 1))}
                disabled={PageNumber === totalPages}
                className="w-8 h-8 flex items-center justify-center rounded-full text-white hover:bg-white/10 transition-colors disabled:opacity-30 disabled:cursor-not-allowed"
              >
                <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 5l7 7-7 7" />
                </svg>
              </button>
            </div>
          </div>
        )}
      </div>
    );
  }, [
    hasSearched,
    flights,
    connectionFlights,
    stopsFilter,
    onShowSearchResults,
    PageNumber,
    totalPages,
    totalFlights,
    date,
    datesInRange,
    onPostRequestSuccess,
    showMobileFilters,
  ]);

  const filterSidebar = (
    <div
      className={`w-[220px] flex-shrink-0 ${hasSearched ? "hidden md:block" : "hidden"}`}
    >
      <div className="sticky top-4">
        {/* Breadcrumb — outside card */}
        <div className="flex items-center gap-3 mb-4 whitespace-nowrap">
          <button
            type="button"
            className="flex items-center justify-center w-8 h-8 rounded-lg bg-white shadow-sm hover:bg-[#F3F4F6] transition-colors flex-shrink-0"
            onClick={() => {
              handleClearSearch();
              onBack?.();
            }}
          >
            <svg
              className="w-4 h-4 text-[#1F2937]"
              fill="none"
              stroke="currentColor"
              viewBox="0 0 24 24"
            >
              <path
                strokeLinecap="round"
                strokeLinejoin="round"
                strokeWidth={2}
                d="M10 19l-7-7m0 0l7-7m-7 7h18"
              />
            </svg>
          </button>
          <span className="text-[#1F2937] text-sm font-bold">{breadcrumbLabel}</span>
          <span className="text-gray-400 text-sm">&gt;</span>
          <span className="text-[#1F2937] text-sm font-medium">Route</span>
        </div>

        {/* Filter card */}
        <div className="bg-white rounded-2xl shadow-sm p-5 min-h-[calc(100vh-200px)]">
          <div className="flex items-center justify-between mb-1">
            <p className="text-[#1F2937] text-base font-bold">Filters</p>
            <button
              type="button"
              className="text-[#9CA3AF] hover:text-[#6B7280] transition-colors"
              title="Reset filters"
              onClick={() => handleStopsFilterChange("none")}
            >
              <svg
                className="w-4 h-4"
                fill="none"
                stroke="currentColor"
                viewBox="0 0 24 24"
              >
                <path
                  strokeLinecap="round"
                  strokeLinejoin="round"
                  strokeWidth={2}
                  d="M4 4v5h.582m15.356 2A8.001 8.001 0 004.582 9m0 0H9m11 11v-5h-.581m0 0a8.003 8.003 0 01-15.357-2m15.357 2H15"
                />
              </svg>
            </button>
          </div>
          <p className="text-[#9CA3AF] text-xs mb-4">
            Showing {totalFlights || 0} results
          </p>
          <p className="text-[#1F2937] text-sm font-semibold mb-3">Stops</p>
          <div className="flex flex-col gap-3">
            {(readOnly
              ? (["none", "one", "two", "three"] as const)
              : (["none", "one", "two", "three", "one-or-fewer"] as const)
            ).map(
              (val) => {
                const labels: Record<string, string> = {
                  none: "Non-stop",
                  one: "1 Stop",
                  two: "2 Stops",
                  three: "3 Stops",
                  "one-or-fewer": "Connected",
                };
                return (
                  <label
                    key={val}
                    className="flex items-center cursor-pointer group"
                  >
                    <div className="flex items-center gap-2">
                      <input
                        type="radio"
                        name="stops-route"
                        checked={stopsFilter === val}
                        onChange={() => handleStopsFilterChange(val)}
                        className="w-4 h-4 text-[#FBBF24] border-gray-300 focus:ring-[#FBBF24] accent-[#1F2937]"
                      />
                      <span className="text-sm text-[#4B5563] group-hover:text-[#1F2937]">
                        {labels[val]}
                      </span>
                    </div>
                  </label>
                );
              },
            )}
          </div>
        </div>
      </div>
    </div>
  );

  return (
    <div className="mt-0">
      <style>{datePickerStyles}</style>

      <div className="flex gap-4 pt-4">
        {filterSidebar}
        <div className="flex-1 min-w-0">
          {/* Tabs + Form wrapped in gif bg */}
          <div
            className={`relative ${hasSearched ? "rounded-2xl mt-4 pb-3" : ""}`}
          >
            {hasSearched && (
              <div className="absolute inset-0 rounded-2xl overflow-hidden">
                <div
                  className="absolute inset-0 bg-cover bg-center"
                  style={{ backgroundImage: `url(${HeroBg})` }}
                />
                <div className="absolute inset-0 bg-gradient-to-b from-black/60 via-black/50 to-black/70" />
              </div>
            )}
            <div className={`relative z-10 ${hasSearched ? "px-3 pt-3" : ""}`}>
              {tabButtons}
            </div>
            <div
              className={`relative z-20 bg-white shadow-xl p-3 overflow-visible ${hasSearched ? "mx-3 mb-3 rounded-2xl rounded-tl-none" : "rounded-2xl rounded-tl-none"}`}
            >
              <div className="flex items-center justify-between">
                <p className="text-[#1F2937] text-sm font-medium">
                  Search by Route
                </p>
                <div className="flex items-center gap-3">
                  <button
                    className="flex items-center gap-1 text-[#9CA3AF] hover:text-[#6B7280] transition-colors"
                    onClick={handleClearSearch}
                    type="button"
                  >
                    <i className="st-close text-xs"></i>
                    <span className="text-xs">Clear</span>
                  </button>
                  <button
                    className="flex items-center gap-1.5 px-3 py-1.5 bg-[#FFFBEB] border border-[#F8F8F8] rounded-lg hover:bg-[#FEF3C7] transition-colors"
                    onClick={handleLastSearchClick}
                    disabled={isLoadingLastSearch}
                    type="button"
                  >
                    <i className="st-previous text-[#192227] text-xs"></i>
                    <span className="font-semibold text-[#192227] text-xs">
                      {isLoadingLastSearch ? "Loading..." : "Last Search"}
                    </span>
                  </button>
                </div>
              </div>
              <div>
                {/* Main fields row: From+To in one box, Date in separate box */}
                <div className="flex items-stretch gap-4 my-2 overflow-visible">
                  {/* From + To container */}
                  <div className="flex items-stretch flex-[2] border border-[#E5E7EB] rounded-xl overflow-visible">
                    {/* From Field */}
                    <div className="relative flex-1 p-3">
                      <label className="flex items-center gap-1.5 mb-1 text-xs text-[#9CA3AF]">
                        <i
                          className="st-flight text-white text-[16px] inline-block"
                          style={{
                            transform: "rotate(-50deg)",
                            WebkitTextStroke: "0.5px #737C8C",
                          }}
                        ></i>
                        Flying From ?
                      </label>
                      <div className="relative">
                        <input
                          type="text"
                          readOnly
                          value={
                            fromLocation
                              ? (() => {
                                  const option = departureOptions.find(
                                    (option) => option.value === fromLocation,
                                  );
                                  if (option) {
                                    return option.label;
                                  }
                                  const recent = recentFromLocations.find(
                                    (loc) => loc.code === fromLocation,
                                  );
                                  if (recent) {
                                    return `${recent.code} - ${recent.name}`;
                                  }
                                  return fromLocation;
                                })()
                              : ""
                          }
                          onClick={() => setIsFromCardOpen(true)}
                          placeholder="Search by Place"
                          className="w-full py-1.5 pr-6 border-none bg-transparent text-sm text-[#1F2937] font-medium cursor-pointer focus:outline-none placeholder-gray-400 truncate"
                        />
                        {fromLocation && (
                          <button
                            type="button"
                            onClick={(e) => {
                              e.stopPropagation();
                              setFromLocation("");
                              setIsFromCardOpen(false);
                              setFromSearchInput("");
                            }}
                            className="absolute right-1 top-1/2 -translate-y-1/2 text-gray-400 hover:text-gray-600 transition-colors"
                          >
                            <i className="st-close text-sm"></i>
                          </button>
                        )}

                        {/* Location Search Card */}
                        <LocationSearchCard
                          type="from"
                          value={fromLocation}
                          onChange={(code, name) => {
                            setFromLocation(code);
                            setErrors((prev) => ({ ...prev, from: undefined }));

                            saveRecentLocation("from", code, name);
                            const updatedRecent = loadRecentLocations("from");
                            setRecentFromLocations(updatedRecent);

                            if (code === toLocation) {
                              setToLocation("");
                              setErrors((prev) => ({
                                ...prev,
                                to: "Departure and arrival locations cannot be the same",
                              }));
                            }

                            setIsFromCardOpen(false);
                            setFromSearchInput("");

                            setTimeout(() => {
                              setIsToCardOpen(true);
                            }, 100);
                          }}
                          recentLocations={recentFromLocations}
                          isOpen={isFromCardOpen}
                          onOpen={() => setIsFromCardOpen(true)}
                          onClose={() => {
                            setIsFromCardOpen(false);
                            setFromSearchInput("");
                          }}
                          placeholder="Search airports..."
                          searchInput={fromSearchInput}
                          setSearchInput={setFromSearchInput}
                          options={departureOptions}
                          isLoading={isLoadingDepartures}
                          label="From"
                          onSearch={loadDepartureCodes}
                        />
                      </div>
                      {errors.from && (
                        <p className="text-red-500 text-[11px] mt-0.5">
                          {errors.from}
                        </p>
                      )}
                    </div>

                    {/* Swap icon */}
                    <div className="flex items-center justify-center px-1">
                      <img src={SwapIcon} alt="Swap" className="w-7 h-7" />
                    </div>

                    {/* To Field */}
                    <div className="relative flex-1 p-3">
                      <label className="flex items-center gap-1.5 mb-1 text-xs text-[#9CA3AF]">
                        <i
                          className="st-flight text-white text-[16px] inline-block"
                          style={{
                            transform: "rotate(-50deg)",
                            WebkitTextStroke: "0.5px #737C8C",
                          }}
                        ></i>
                        Going to ?
                      </label>
                      <div className="relative">
                        <input
                          type="text"
                          readOnly
                          value={
                            toLocation
                              ? (() => {
                                  const option = arrivalOptions.find(
                                    (option) => option.value === toLocation,
                                  );
                                  if (option) {
                                    return option.label;
                                  }
                                  const recent = recentToLocations.find(
                                    (loc) => loc.code === toLocation,
                                  );
                                  if (recent) {
                                    return `${recent.code} - ${recent.name}`;
                                  }
                                  return toLocation;
                                })()
                              : ""
                          }
                          onClick={() => setIsToCardOpen(true)}
                          placeholder="Search by Place"
                          className="w-full py-1.5 pr-6 border-none bg-transparent text-sm text-[#1F2937] font-medium cursor-pointer focus:outline-none placeholder-gray-400 truncate"
                        />
                        {toLocation && (
                          <button
                            type="button"
                            onClick={(e) => {
                              e.stopPropagation();
                              setToLocation("");
                              setIsToCardOpen(false);
                              setToSearchInput("");
                            }}
                            className="absolute right-1 top-1/2 -translate-y-1/2 text-gray-400 hover:text-gray-600 transition-colors"
                          >
                            <i className="st-close text-sm"></i>
                          </button>
                        )}

                        {/* Location Search Card */}
                        <LocationSearchCard
                          type="to"
                          value={toLocation}
                          onChange={(code, name) => {
                            setToLocation(code);
                            setErrors((prev) => ({ ...prev, to: undefined }));

                            saveRecentLocation("to", code, name);
                            const updatedRecent = loadRecentLocations("to");
                            setRecentToLocations(updatedRecent);

                            if (code === fromLocation) {
                              setFromLocation("");
                              setErrors((prev) => ({
                                ...prev,
                                from: "Departure and arrival locations cannot be the same",
                              }));
                            }

                            setIsToCardOpen(false);
                            setToSearchInput("");
                            lastToSearchRef.current = "";

                            setTimeout(() => {
                              setIsCalendarOpen(true);
                            }, 100);
                          }}
                          recentLocations={recentToLocations}
                          isOpen={isToCardOpen}
                          onOpen={() => setIsToCardOpen(true)}
                          onClose={() => {
                            setIsToCardOpen(false);
                            setToSearchInput("");
                            lastToSearchRef.current = "";
                          }}
                          placeholder="Search airports..."
                          searchInput={toSearchInput}
                          setSearchInput={setToSearchInput}
                          options={arrivalOptions}
                          isLoading={isLoadingArrivals}
                          label="To"
                          onSearch={loadArrivalCodes}
                        />
                      </div>
                      {errors.to && (
                        <p className="text-red-500 text-[11px] mt-0.5">
                          {errors.to}
                        </p>
                      )}
                    </div>
                  </div>

                  {/* Date field - separate bordered box */}
                  <div className="relative flex-1 p-3 border border-[#E5E7EB] rounded-xl">
                    <label className="flex items-center gap-1.5 mb-1 text-xs text-[#9CA3AF]">
                      <i className="st-calendar text-[#9CA3AF] text-[11px]"></i>
                      Date
                    </label>
                    <div className="relative">
                      <DatePicker
                        selected={null}
                        onChange={handleDateChange}
                        onCalendarClose={() => setIsCalendarOpen(false)}
                        onCalendarOpen={() => setIsCalendarOpen(true)}
                        open={isCalendarOpen}
                        onClickOutside={() => setIsCalendarOpen(false)}
                        shouldCloseOnSelect={false}
                        minDate={new Date()}
                        placeholderText={`${datesInRange.length > 0 ? `Selected Dates (${datesInRange.length})` : "Select travel dates"}`}
                        className={`block w-full py-1.5 border-none bg-transparent text-sm text-[#1F2937] font-medium ${datesInRange.length > 0 ? "placeholder-black" : "placeholder-gray-400"} focus:outline-none cursor-pointer`}
                        highlightDates={selectedDates}
                        inline={false}
                        wrapperClassName="w-full"
                        popperPlacement="bottom-end"
                        portalId="datepicker-portal"
                        onInputClick={() => setIsCalendarOpen(true)}
                      >
                        <div className="flex justify-between w-full px-2 pt-2">
                          <button
                            onClick={(e) => {
                              e.preventDefault();
                              clearAllDates();
                            }}
                            className="px-4 py-2 text-sm text-red-600 border border-red-600 rounded-lg hover:bg-red-50"
                            type="button"
                          >
                            Clear All
                          </button>
                          <button
                            onClick={(e) => {
                              e.preventDefault();
                              handleSaveDates();
                            }}
                            className="px-4 py-2 text-sm text-white bg-[#FFBC00] rounded-lg hover:bg-[#e6a800]"
                            type="button"
                          >
                            Save ({selectedDates.length})
                          </button>
                        </div>
                      </DatePicker>
                      {datesInRange.length > 0 && (
                        <button
                          onClick={() => setIsCalendarOpen(true)}
                          className="absolute inset-y-0 end-0 flex items-center z-20 px-1 text-lg cursor-pointer text-gray-400 hover:text-blue-600 rounded-e-md focus:outline-none"
                          type="button"
                          title="Edit dates"
                        >
                          <i className="st-calendar text-lightgray-200"></i>
                        </button>
                      )}
                    </div>
                    {errors.date && (
                      <p className="text-red-500 text-[11px] mt-0.5">
                        {errors.date}
                      </p>
                    )}
                  </div>
                </div>

                {/* Include nearby toggles + Find button row */}
                <div className="flex flex-wrap items-stretch justify-between gap-3 mb-2">
                  {!readOnly && (
                  <div className="flex flex-wrap items-stretch gap-3">
                    {/* Include nearby Departure Airports */}
                    <div className="flex items-center gap-3 px-4 py-3 border border-[#E5E7EB] rounded-xl">
                      <i
                        className="st-flight text-white text-[16px] inline-block"
                        style={{
                          transform: "rotate(-50deg)",
                          WebkitTextStroke: "0.5px #737C8C",
                        }}
                      ></i>
                      <div className="flex flex-col">
                        <span className="text-[11px] text-[#9CA3AF] leading-tight">
                          Include nearby
                        </span>
                        <span className="text-xs text-[#1F2937] font-semibold leading-tight">
                          Departure Airports
                        </span>
                      </div>
                      <button
                        type="button"
                        onClick={() =>
                          setSelectedDeparture(
                            selectedDeparture === "yes" ? "no" : "yes",
                          )
                        }
                        className={`relative inline-flex h-6 w-11 items-center rounded-full transition-colors duration-200 ${
                          selectedDeparture === "yes"
                            ? "bg-[#FBBF24]"
                            : "bg-gray-300"
                        }`}
                      >
                        <span
                          className={`inline-block h-4 w-4 transform rounded-full bg-white shadow transition-transform duration-200 ${
                            selectedDeparture === "yes"
                              ? "translate-x-6"
                              : "translate-x-1"
                          }`}
                        />
                      </button>
                    </div>

                    {/* Include nearby Arrival Airports */}
                    <div className="flex items-center gap-3 px-4 py-3 border border-[#E5E7EB] rounded-xl">
                      <i
                        className="st-flight text-white text-[16px] inline-block"
                        style={{
                          transform: "rotate(-50deg)",
                          WebkitTextStroke: "0.5px #737C8C",
                        }}
                      ></i>
                      <div className="flex flex-col">
                        <span className="text-[11px] text-[#9CA3AF] leading-tight">
                          Include nearby
                        </span>
                        <span className="text-xs text-[#1F2937] font-semibold leading-tight">
                          Arrival Airports
                        </span>
                      </div>
                      <button
                        type="button"
                        onClick={() =>
                          setSelectedArrival(
                            selectedArrival === "yes" ? "no" : "yes",
                          )
                        }
                        className={`relative inline-flex h-6 w-11 items-center rounded-full transition-colors duration-200 ${
                          selectedArrival === "yes"
                            ? "bg-[#FBBF24]"
                            : "bg-gray-300"
                        }`}
                      >
                        <span
                          className={`inline-block h-4 w-4 transform rounded-full bg-white shadow transition-transform duration-200 ${
                            selectedArrival === "yes"
                              ? "translate-x-6"
                              : "translate-x-1"
                          }`}
                        />
                      </button>
                    </div>
                  </div>
                  )}

                  <button
                    className={`px-8 py-3 rounded-xl text-[#192227] text-center bg-[#FBBF24] hover:bg-[#F59E0B] text-sm font-semibold tracking-wide flex items-center justify-center gap-2 transition-colors ${
                      isLoadingFlights ? "opacity-75 cursor-not-allowed" : ""
                    }`}
                    onClick={handleSearchClick}
                    disabled={isLoadingFlights}
                  >
                    {isLoadingFlights ? (
                      <>
                        <div className="w-5 h-5 border-2 border-[#192227] border-t-transparent rounded-full animate-spin"></div>
                        <span>Searching...</span>
                      </>
                    ) : (
                      <>
                        <svg
                          className="w-4 h-4"
                          fill="none"
                          stroke="currentColor"
                          viewBox="0 0 24 24"
                        >
                          <path
                            strokeLinecap="round"
                            strokeLinejoin="round"
                            strokeWidth={2}
                            d="M21 21l-6-6m2-5a7 7 0 11-14 0 7 7 0 0114 0z"
                          />
                        </svg>
                        Find Your Flight
                      </>
                    )}
                  </button>
                </div>
              </div>
            </div>
          </div>
          {searchResults}
        </div>
      </div>
    </div>
  );
};

export default SearchByRoute;
